import React from 'react';
import { Button, FeatureTrialNotice, FeatureUnavailableBanner, Loader, PageBody, PageTitle } from 'src/components';
import { useFeatureTrialsQuery } from 'src/api/queries/subscriptionQueries';
import useModal from 'src/hooks/useModal';
import { useAlerts } from 'src/common/AlertManager';
import { useFeatureAvailability } from 'src/hooks/useFeatureAvailability';
import { useDeliveryInsightsEnabled } from 'src/hooks/useDeliveryInsightsEnabled';
import { isErrorResponseResult } from 'src/api/utils/errorHandlers';
import { useDestinationsQuery } from 'src/api/queries/analyticsDestinationsQueries';
import {
  useSetDestinationEnabledMutation,
  useDeleteDestinationMutation,
  useTestDestinationMutation,
} from 'src/api/mutations/analyticsDestinationsMutations';
import {
  AnalyticsDestination,
  DestinationType,
} from 'src/types/TAnalyticsDestinations';
import AddDestinationModal from './AddDestinationModal';
import styles from './styles.module.scss';
import { planUpgradeRequired } from 'src/api/utils/errorHandlers';
import { PlanUpgradeBanner } from 'src/components/FeatureUnavailable/PlanUpgradeBanner';

const TYPE_LABEL: Record<DestinationType, string> = {
  ga4: 'Google Analytics 4',
  segment: 'Segment',
};

const ALL_TYPES: DestinationType[] = ['ga4', 'segment'];

const fmtDate = (raw: string | null): string => {
  if (!raw) return 'never';
  const d = new Date(raw);
  return Number.isNaN(d.getTime())
    ? raw
    : d.toLocaleString(undefined, {
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
};

const DestinationCard: React.FC<{
  type: DestinationType;
  existing?: AnalyticsDestination;
  /** Read-only mode — the backend feature isn't available yet. */
  disabled?: boolean;
}> = ({ type, existing, disabled = false }) => {
  const { showModal } = useModal();
  const { showSuccess, showAlert } = useAlerts();
  const setEnabled = useSetDestinationEnabledMutation();
  const remove = useDeleteDestinationMutation();
  const test = useTestDestinationMutation();

  const openModal = () =>
    showModal(<AddDestinationModal destinationType={type} existing={existing} />);

  const handleToggle = () => {
    if (!existing) return;
    setEnabled.mutate(
      { destinationType: type, enabled: !existing.enabled },
      { onError: () => showAlert('Could not update destination.') },
    );
  };

  const handleRemove = () => {
    if (!existing) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove the ${TYPE_LABEL[type]} destination?`)) return;
    remove.mutate(type, {
      onSuccess: (result) => {
        if (isErrorResponseResult(result)) {
          showAlert('Could not remove destination.');
          return;
        }
        showSuccess(`${TYPE_LABEL[type]} destination removed.`);
      },
      onError: () => showAlert('Could not remove destination.'),
    });
  };

  const handleTest = () => {
    test.mutate(
      { destination_type: type },
      {
        onSuccess: (result) => {
          if (isErrorResponseResult(result)) {
            showAlert('Test request failed.');
            return;
          }
          if (result.ok) showSuccess(result.message);
          else showAlert(result.message);
        },
        onError: () => showAlert('Test request failed.'),
      },
    );
  };

  if (!existing || !existing.configured) {
    return (
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h3 className={styles.cardTitle}>{TYPE_LABEL[type]}</h3>
          <span className={styles.statusMuted}>Not configured</span>
        </div>
        <p className={styles.cardDesc}>
          {type === 'ga4'
            ? 'Forward CDN delivery events to a GA4 property (Measurement Protocol).'
            : 'Forward CDN delivery events to Segment (HTTP Track API).'}
        </p>
        <Button mode="primary" outlined onClick={openModal} disabled={disabled}>
          Add {TYPE_LABEL[type]}
        </Button>
      </div>
    );
  }

  const autoDisabled = !existing.enabled && existing.consecutive_failures >= 10;

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{TYPE_LABEL[type]}</h3>
        <span className={existing.enabled ? styles.statusOk : styles.statusOff}>
          {existing.enabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>

      <dl className={styles.meta}>
        {type === 'ga4' && (
          <>
            <dt>Measurement ID</dt>
            <dd className={styles.mono}>{existing.measurement_id || '—'}</dd>
          </>
        )}
        <dt>Last pushed</dt>
        <dd>{fmtDate(existing.last_pushed_at)}</dd>
        {existing.consecutive_failures > 0 && (
          <>
            <dt>Consecutive failures</dt>
            <dd className={styles.warn}>{existing.consecutive_failures}</dd>
          </>
        )}
      </dl>

      {existing.last_error && <div className={styles.errorNote}>{existing.last_error}</div>}
      {autoDisabled && (
        <div className={styles.errorNote}>
          Auto-disabled after repeated failures. Update the credentials and re-enable.
        </div>
      )}

      <div className={styles.cardActions}>
        <Button mode="secondary" outlined loading={test.isLoading} onClick={handleTest} disabled={disabled}>
          Test
        </Button>
        <Button mode="secondary" outlined onClick={openModal} disabled={disabled}>
          Edit
        </Button>
        <Button mode="secondary" outlined loading={setEnabled.isLoading} onClick={handleToggle} disabled={disabled}>
          {existing.enabled ? 'Disable' : 'Enable'}
        </Button>
        <Button mode="decline" outlined loading={remove.isLoading} onClick={handleRemove} disabled={disabled}>
          Remove
        </Button>
      </div>
    </div>
  );
};

const AnalyticsDestinations: React.FC = () => {
  const { data, isLoading, error } = useDestinationsQuery();
  const featureStatus = useFeatureAvailability('analytics-destinations');
  const deliveryAddonEnabled = useDeliveryInsightsEnabled();

  // The addons blob doesn't know about feature trials — an account
  // mid-DELIVERY_INSIGHTS-trial is served by the API while the blob still
  // says no (same blind spot the Delivery Insights page had). Ask the
  // trials endpoint when the blob says no.
  const { data: featureTrials } = useFeatureTrialsQuery({
    enabled: !deliveryAddonEnabled,
  });
  const deliveryTrialActive = Boolean(
    featureTrials?.trials?.some(
      (t) => t.feature_code === 'DELIVERY_INSIGHTS' && t.active,
    ),
  );
  const deliveryEntitled = deliveryAddonEnabled || deliveryTrialActive;
  const deliveryTrialAvailable = Boolean(
    featureTrials?.trials_enabled &&
      featureTrials?.available_features?.includes('DELIVERY_INSIGHTS'),
  );

  // Read-only mode when delivery insights isn't entitled (addon or active
  // trial), or while the backend API is missing on the old release (or
  // reports the feature unavailable): keep the page visible but disable the
  // write actions. Auto-recovers once the addon/API are available.
  const errorStatus = error && isErrorResponseResult(error) ? error.status : undefined;
  const unavailable =
    !deliveryEntitled || featureStatus === 'unavailable' || errorStatus === 404;

  const byType: Partial<Record<DestinationType, AnalyticsDestination>> = {};
  (data?.destinations ?? []).forEach((d) => {
    byType[d.destination_type] = d;
  });

  return (
    <div className={styles.pageWrapper}>
      <PageTitle title="Analytics Integrations" className={styles.pageTitle} />
      <PageBody>
        {/* Two trials can power this page: its own feature, and the
            delivery-insights data it rides on. Each notice self-hides when
            that trial isn't active. */}
        <FeatureTrialNotice featureCode="ANALYTICS_INTEGRATIONS" label="Analytics Integrations" />
        <FeatureTrialNotice featureCode="DELIVERY_INSIGHTS" label="Delivery Insights" />
        {/* Not entitled to the underlying delivery data → the standard
            plan-upgrade banner (with the trial CTA when this account can
            still start one); infra/rollout gaps keep the generic banner. */}
        {!deliveryEntitled ? (
          <PlanUpgradeBanner
            gate={{
              feature: 'delivery_insights',
              requiredPlan: 'STARTER',
              addonCode: 'DELIVERY_INSIGHTS',
              trialAvailable: deliveryTrialAvailable,
              trialDays: featureTrials?.trial_days,
            }}
          />
        ) : (
          unavailable && <FeatureUnavailableBanner />
        )}

        <p className={styles.intro}>
          Forward your CDN delivery events to your own analytics tools. Events are pushed
          per asset and hour after FileSpin ingests CDN logs. Credentials are encrypted at
          rest and never shown again after saving.
        </p>

        <div className={styles.explainer}>
          <h3>What we push, and why</h3>
          <ul>
            <li>
              <strong>What:</strong> one aggregated event per asset, per hour, per
              campaign/channel/placement/variant/audience — never one event per request.
            </li>
            <li>
              <strong>Why aggregate:</strong> per-request would blow past GA4/Segment event
              quotas and cost; the hourly rollup fits both tools’ event models.
            </li>
            <li>
              <strong>Included:</strong> delivery counts and bytes, top country/device,
              delivered-format mix, and campaign/channel attribution — mapped to the standard{' '}
              <code>source</code>/<code>medium</code>/<code>campaign</code> fields plus{' '}
              <code>filespin_*</code> properties.
            </li>
            <li>
              <strong>Not included:</strong> no GA4 sessions or users, no unique visitors, no
              PII. These are server-side delivery aggregates that complement your own
              client-side analytics.
            </li>
          </ul>
        </div>

        {isLoading ? (
          <div className={styles.sectionLoading}>
            <Loader />
          </div>
        ) : error && !unavailable && planUpgradeRequired(error) ? (
          <PlanUpgradeBanner gate={planUpgradeRequired(error)!} />
        ) : error && !unavailable ? (
          <div className={styles.errorBanner}>
            {errorStatus === 403
              ? 'You do not have permission to manage analytics destinations.'
              : 'Failed to load destinations.'}
          </div>
        ) : (
          <div className={styles.cardGrid}>
            {ALL_TYPES.map((type) => (
              <DestinationCard
                key={type}
                type={type}
                existing={byType[type]}
                disabled={unavailable}
              />
            ))}
          </div>
        )}
      </PageBody>
    </div>
  );
};

export default AnalyticsDestinations;
